import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useFavorites } from '../context/FavoritesContext';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const ProductCard = ({ product }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const { addToCart, setIsCartOpen } = useCart();
  const { user, setIsLoginModalOpen } = useAuth();
  const { isFavorite, toggleFavorite } = useFavorites();

  const handleImageHover = () => {
    if (product.images && product.images.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % product.images.length);
    }
  };

  const handleImageLeave = () => {
    setCurrentImageIndex(0);
  };

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedSize) {
      alert('Пожалуйста, выберите размер');
      return;
    }
    addToCart(product, selectedSize);
    setIsCartOpen(true);
  };

  const handleFavoriteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      setIsLoginModalOpen(true);
      return;
    }
    toggleFavorite(product.id);
  };

  const isLiked = isFavorite(product.id);
  const hasImages = product.images && product.images.length > 0;
  const currentImage = hasImages ? product.images[currentImageIndex] : null;

  // Функция для безопасного получения URL изображения
  const safeImageUrl = (url) => {
    if (!url) return '/placeholder.jpg';
    return url.startsWith('/') ? url : '/placeholder.jpg';
  };

  return (
    <div className="group">
      <Link to={`/art/${product.article}`} className="block">
        {/* Image Container */}
        <div
          className="relative w-full overflow-hidden mb-2 rounded-lg"
          style={{ aspectRatio: '1/1', maxHeight: '100%' }}
          onMouseEnter={handleImageHover}
          onMouseLeave={handleImageLeave}
        >
          {currentImage ? (
            <img
              src={safeImageUrl(currentImage)}
              alt={product.name}
              onError={(e) => { e.target.src = '/placeholder.jpg'; }}
              className="w-full h-full object-contain"
              style={{ maxHeight: '100%', width: 'auto', margin: '0 auto' }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-brand-text text-sm dark:text-brand-text">
              Нет фото
            </div>
          )}
          {/* Favorite Button */}
          <button
            onClick={handleFavoriteClick}
            className="absolute top-2 right-2 p-1.5 glass-button rounded-lg hover:scale-105 transition-all shadow-lg bg-brand-surface/80 dark:bg-brand-surface/80 backdrop-blur-sm"
          >
            <Heart
              className={`w-4 h-4 transition-colors ${isLiked ? 'fill-brand-accent text-brand-text' : 'text-brand-text dark:text-brand-text'}`}
              strokeWidth={1.5}
            />
          </button>
          {/* Sale Badge */}
          {product.old_price && (
            <div className="absolute top-2 left-2 bg-brand-accent text-brand-text px-2 py-0.5 text-[10px] font-medium rounded-md">
              SALE
            </div>
          )}
          {/* Image Indicators */}
          {hasImages && product.images.length > 1 && (
            <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex gap-1">
              {product.images.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1 h-1 rounded-full transition-colors ${
                    idx === currentImageIndex ? 'bg-brand-surface' : 'bg-brand-surface/40'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </Link>

      {/* Product Info */}
      <div className="space-y-1">
        <p className="text-[10px] text-brand-text dark:text-brand-text">арт: {product.article}</p>
        <Link to={`/art/${product.article}`}>
          <h3 className="font-medium text-xs sm:text-sm text-brand-text dark:text-brand-text hover:text-brand-text dark:hover:text-brand-text transition-colors line-clamp-1">
            {product.name}
          </h3>
        </Link>

        {/* Price */}
        <div className="flex items-center gap-1">
          <span className="font-semibold text-sm text-brand-text dark:text-brand-text">
            {product.price?.toLocaleString('ru-RU')} ₽
          </span>
          {product.old_price && (
            <span className="text-[10px] text-brand-text dark:text-brand-text line-through">
              {product.old_price.toLocaleString('ru-RU')} ₽
            </span>
          )}
        </div>

        {/* Size Selector */}
        {product.sizes && product.sizes.length > 0 && (
          <div className="flex gap-1 items-center" onClick={(e) => e.stopPropagation()}>
            <Select value={selectedSize} onValueChange={setSelectedSize}>
              <SelectTrigger className="w-16 h-7 text-xs rounded-md dark:bg-brand-surface dark:border-brand-border dark:text-brand-text">
                <SelectValue placeholder="Разм." />
              </SelectTrigger>
              <SelectContent className="dark:bg-brand-surface dark:border-brand-border">
                {product.sizes.map((size) => (
                  <SelectItem 
                    key={size} 
                    value={size}
                    className="dark:bg-brand-surface dark:text-brand-text dark:focus:bg-brand-surface"
                  >
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              onClick={handleAddToCart}
              className="flex-1 h-7 bg-brand-accent dark:bg-brand-accent text-brand-text dark:text-brand-text hover:bg-brand-surface dark:hover:bg-brand-surface text-xs font-medium rounded-md"
            >
              В корзину
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCard;